import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export interface ImageDto {
  id: string;
  fileName: string;
  url: string;
  thumbnailUrl?: string | null;
  size: number;
  mimeType: string;
  width?: number | null;
  height?: number | null;
  createdAt: string;
}

export interface ParagraphImageDto {
  referenceId: string;
  imageId: string;
  url: string;
  thumbnailUrl?: string | null;
  position: string;
  sequenceOrder: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export function useImages(search?: string) {
  const { getToken } = useAuth();
  return useQuery<ImageDto[]>({
    queryKey: ['images', search ?? ''],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const query = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await client
        .get(`api/v2/images${query}`)
        .json<ApiResponse<ImageDto[]>>();
      return response.data ?? [];
    },
  });
}

export function useUploadImage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const client = createApiClient(getToken);
      const form = new FormData();
      form.append('file', file);
      const response = await client
        .post('api/v2/images', { body: form, timeout: 120_000 })
        .json<ApiResponse<ImageDto>>();
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['images'] }),
  });
}

export function useDeleteImage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (imageId: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/images/${imageId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['images'] }),
  });
}

export function useBatchDeleteImages() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const client = createApiClient(getToken);
      await client.post('api/v2/images/batch-delete', {
        json: { ids },
        timeout: 300_000,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['images'] }),
  });
}

export function useParagraphImages(paragraphId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<ParagraphImageDto[]>({
    enabled: !!paragraphId,
    queryKey: ['paragraph-images', paragraphId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get(`api/v2/paragraphs/${paragraphId}/images`)
        .json<ApiResponse<ParagraphImageDto[]>>();
      return response.data ?? [];
    },
  });
}

export function useLinkImage(paragraphId: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { imageId: string; position?: string }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/paragraphs/${paragraphId}/images`, {
          json: {
            imageId: input.imageId,
            position: input.position ?? 'inline',
          },
        })
        .json<ApiResponse<unknown>>();
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['paragraph-images', paragraphId],
      }),
  });
}

export function useUnlinkImage(paragraphId: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (referenceId: string) => {
      const client = createApiClient(getToken);
      await client.delete(
        `api/v2/paragraphs/${paragraphId}/images/${referenceId}`,
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['paragraph-images', paragraphId],
      }),
  });
}

export function useReorderParagraphImages(paragraphId: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderedReferenceIds: string[]) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/paragraphs/${paragraphId}/images/reorder`, {
        json: { orderedReferenceIds },
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['paragraph-images', paragraphId],
      }),
  });
}
